/* eslint-disable max-len */
//time edit control
//concept of implementing ngModel came from https://embed.plnkr.co/nqKUSPWb6w5QXr8a0wEu/?show=preview
//Uses moment.js for time parsing/conversion  https://momentjs.com/docs/#/parsing/
//TODO implement property to specify display mask
//TODO implement property to specify data format mask

import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';

const noop = () => { };

/**
 * Sure MGA Time Input Component with dropdown time.
 *
 * @example
 * To show timepicker when using ep-field:
 *       <ep-field
 *           columnSpan="2"
 *           label="Business Hours Start"
 *           format="time"
 *           controlPath="Article.Business_Hours_Start"
 *         ></ep-field>
 *
 */

@Component({
    standalone: false,
    selector: 'ep-time-input',
    template: `
    <div class="unity-form-group date" style="margin:0px; padding:0px">
      <label *ngIf="!noLabel"><ng-content></ng-content></label>
      <div #dpGroup class="unity-form-row" style="position:relative;">
        <input
          class="unity-date"
          #inputElement
          [(ngModel)]="currentInput"
          (ngModelChange)="inputChanged($event)"
          [mask]="timeMask"
          [specialCharacters]="specialCharacters"
          [dropSpecialCharacters]="dropSpecialCharacters"
          [apm]="true"
          [class]="!disabled ? 'form-control' : ''"
          [disabled]="disabled"
          (keyup.f4)="onKeyF4()"
          (focus)="onFocus()"
          (blur)="onBlur()"
          size="15"
        />
        <aurora-icon icon="calendar" #toggleButton (click)="disabled ? null : toggleTimePopup()" tabindex="-1" [style.cursor]="disabled ? 'not-allowed' : ''"></aurora-icon>
      </div>

      <ngb-timepicker
        *ngIf="isTimePopupOpen"
        [hourStep]="hourStep"
        [minuteStep]="minuteStep"
        [meridian]="meridian"
        [readonlyInputs]="disabled"
        tabindex="-1"
        (click)="$event.preventDefault(); $event.stopPropagation()"
        [(ngModel)]="currentTime"
        style="position: absolute;z-index: 3;background-color: var(--aurora-primitive-gray-005);top: var(--height-input);min-width: 100%;border: 1px solid var(--aurora-primitive-gray-010);padding: 0.5rem 0;"
        (ngModelChange)="timePickerChanged($event)"
      ></ngb-timepicker>
    </div>
  `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: TimeInputComponent, multi: true }]
})
export class TimeInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy, OnInit {
    // Inputs for timepicker control
    @Input() meridian: boolean = true;
    @Input() hourStep = 1;
    @Input() minuteStep = 1;
    @Input() noLabel: boolean = false;

    @ViewChild('dpGroup', { static: true })
    dpGroup: ElementRef;

    @ViewChild('toggleButton', { static: true })
    toggleButton: ElementRef;

    public currentTime: NgbTimeStruct;

    public currentInput: string;

    private innerValue: any = '';

    private displayFormat = 'hh:mm A';
    private dataFormat = 'HH:mm';

    // ngx-mask properties
    public timeMask = `Hh:m0 UU`;
    public specialCharacters = [':'];
    public dropSpecialCharacters = false;

    //Placeholders for the callbacks which are later provided
    //by the Control Value Accessor
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    /**
     * Input to control whether time picker automatically displays when time picker input gets focus. Default is true.
     */
    @Input()
    popupOnFocus: boolean = true;

    /**
     * Input to indicate whether input should be disabled.  Default is false.
     */
    @Input()
    disabled: boolean = false;

    @Output() blur = new EventEmitter();

    @Output() focus = new EventEmitter();

    @Output() calendarClosed = new EventEmitter();

    isTimePopupOpen = false;
    unlistenCallback: any;

    constructor(private renderer: Renderer2, private cdRef: ChangeDetectorRef) { }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: any) {
        if (this.isEscapeKey(event)) {
            if (this.isTimePopupOpen) {
                this.isTimePopupOpen = false;
                this.calendarClosed.emit();
            }
            event.preventDefault();
            event.stopPropagation();
        } else if (this.isTabOrShiftTabKey(event)) {
            this.isTimePopupOpen = false;
        }
    }

    isEscapeKey(event: KeyboardEvent): boolean {
        return event.key == 'Escape' || event.key == 'Esc';
    }

    isTabOrShiftTabKey(event: KeyboardEvent): boolean {
        return event.key == 'Tab' || (event.shiftKey && event.key == 'Tab');
    }

    ngOnInit() {
        this.displayFormat = 'hh:mm A';
    }

    ngAfterViewInit() {
        this.unlistenCallback = this.renderer.listen('window', 'click', (e: Event) => {
            if (!this.toggleButton.nativeElement.contains(e.target) && !this.dpGroup.nativeElement.contains(e.target)) {
                this.isTimePopupOpen = false;
                this.cdRef.markForCheck();
            }
        });
    }

    ngOnDestroy() {
        this.unlistenCallback();
    }

    public inputChanged(inputVal: string) {
        let timestring = inputVal;

        let m = moment(timestring, this.displayFormat);
        if (m && m.isValid()) {
            this.value = m.format(this.dataFormat);
            this.currentTime = { hour: m.hour(), minute: m.minute(), second: 0 };
        } else {
            this.value = '';
            this.currentTime = null;
            this.currentInput = '';
        }
    }

    public timePickerChanged(timeVal: NgbTimeStruct) {
        let newTime: moment.Moment;

        if (!this.currentTime) {
            newTime = moment();
            this.currentTime = {
                hour: newTime.hour(),
                minute: newTime.minute(),
                second: 0
            };
        } else {
            this.currentTime = {
                hour: timeVal.hour,
                minute: timeVal.minute,
                second: 0
            };
        }

        if (!newTime) {
            newTime = moment();
        }

        let currTime: NgbTimeStruct = {
            hour: timeVal ? timeVal.hour : newTime.hour(),
            minute: timeVal ? timeVal.minute : newTime.minute(),
            second: 0
        };

        let m = moment.parseZone({
            hour: currTime.hour,
            minute: currTime.minute,
            second: currTime.second
        });
        if (m && m.isValid()) {
            this.value = m.format(this.dataFormat);
            this.currentInput = m.format(this.displayFormat);
        } else {
            this.value = '';
            this.currentTime = null;
            this.currentInput = '';
        }

        // this.cdRef.detectChanges();
        this.onTouchedCallback();
        this.blur.emit();
    }

    ////get accessor
    private get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    private set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }

    public onBlur() {
        let m = moment.parseZone(this.currentInput, this.displayFormat);
        let minValid: boolean = true;
        let maxValid: boolean = true;

        if (m && m.isValid() && minValid && maxValid) {
            this.value = m.format(this.dataFormat);
            this.currentInput = m.format(this.displayFormat);
            this.currentTime = { hour: m.hour(), minute: m.minute(), second: 0 };
        } else {
            this.value = '';
            this.currentTime = null;
            this.currentInput = '';
        }

        this.blur.emit();
        this.onTouchedCallback();
    }

    public onKeyF4() {
        this.toggleTimePopup();
    }

    public onFocus() {
        if (this.popupOnFocus && !this.isTimePopupOpen) {
            this.isTimePopupOpen = true;
            this.focus.emit();
        }
    }

    toggleTimePopup() {
        this.isTimePopupOpen = !this.isTimePopupOpen;
    }

    //From ControlValueAccessor interface
    writeValue(value: any) {
        if (value !== this.innerValue) {
            this.innerValue = value;

            let m = moment(value, this.displayFormat);
            if (value && value > '' && m && m.isValid()) {
                this.currentInput = m.format(this.displayFormat);

                let timeVal = {
                    hour: m.hour() ? m.hour() : 0,
                    minute: m.minute() ? m.minute() : 0,
                    second: 0
                };
                this.currentTime = { hour: timeVal.hour, minute: timeVal.minute, second: timeVal.second };
            } else {
                this.currentInput = null;
                this.currentTime = null;
            }
            this.cdRef.detectChanges();
        }
    }

    //From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
